import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EntityTypeFeaturedIcon } from "@/features/entity-types/components/entity-type-featured-icon";
import { placeFormColumn } from "@/features/entity-types/lib/place-form-column";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useRouterState,
} from "@tanstack/react-router";
import {
	Copy01,
	Edit05,
	Expand03,
	LayersThree01,
	Trash02,
} from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Label } from "@/components/base/label";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	TableCellContent,
	TableCellContentType,
} from "@/components/Molecules/Table/table-cell-content";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { getActionsColumnHeader } from "@/components/Molecules/Table/table-column-headers";
import { getTableRowId } from "@/components/Molecules/Table/table-column-meta";
import { CreateRecordTypeModal } from "@/components/Organisms/CreateRecordTypeModal/CreateRecordTypeModal";
import { DeleteRecordTypeModal } from "@/components/Organisms/DeleteRecordTypeModal/DeleteRecordTypeModal";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { EMPTY_ORG_ID } from "@/constants/org";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useCanCreateFormLayout } from "@/features/form-editor/hooks/use-can-create-form-layout";
import { useTableSettings } from "@/hooks/use-table-settings";
import { authQueryOptions } from "@/services/auth/auth.options";
import {
	useDuplicateRecordType,
	useExportRecordTypes,
} from "@/services/record/record.mutations";
import {
	recordKeys,
	recordQueryOptions,
} from "@/services/record/record.options";
import type {
	RecordType,
	RecordTypesListParams,
} from "@/services/record/record.types";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { resolveIconComponent } from "@/utils/icon-catalog";
import { showApiErrorToast } from "@/utils/toast.utils";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/configuration/recordTypes",
)({
	component: RouteComponent,
});

const TABLE_KEY = "admin.configuration.recordTypes";

const DEFAULT_COLUMN_ORDER = ["name", "description", "source", "form", "id", "actions"];
const DEFAULT_SORTING = [{ id: "name", desc: false }];
const DEFAULT_PAGE_SIZE = 10;
const ACTIONS_COLUMN_ID = "actions";

const normalizeColumnOrder = (columnOrder?: string[]) => {
	const nonActionDefaultColumns = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => columnId !== ACTIONS_COLUMN_ID,
	);

	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...nonActionDefaultColumns, ACTIONS_COLUMN_ID];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter(
		(columnId) =>
			allowedColumns.has(columnId) && columnId !== ACTIONS_COLUMN_ID,
	);
	const unique = placeFormColumn(Array.from(new Set(sanitized)));
	const missingDefaults = nonActionDefaultColumns.filter(
		(columnId) => columnId !== "form" && !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults, ACTIONS_COLUMN_ID];
};

const serverSortFieldMapping: Record<string, string[]> = {
	name: ["Name"],
	description: ["Description"],
	id: ["Id"],
};

function resolveRecordTypeIcon(icon?: string | null) {
	return resolveIconComponent(icon ?? "") ?? LayersThree01;
}

function RecordTypeDeleteAction({
	rt,
	onDelete,
}: {
	rt: RecordType;
	onDelete: () => void;
}) {
	const { t } = useAppTranslation();
	const canDelete = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteRecordType",
			entityType: "RecordType",
			entityId: rt.id,
		}),
	});

	const recordsCountQuery = useQuery({
		...recordQueryOptions.recordsCount({ RecordTypeIds: [rt.id] }),
		enabled: !!canDelete.data,
	});

	if (!canDelete.data) return null;
	if (recordsCountQuery.data == null || recordsCountQuery.data > 0) return null;

	return (
		<>
			<Dropdown.Separator />
			<Dropdown.Item
				id="delete"
				label={t("admin.recordTypes.table.actions.delete")}
				icon={Trash02}
				className="[&_span]:text-red-600"
				onAction={onDelete}
			/>
		</>
	);
}

function RecordTypesTable({
	tableOptionsPortalTarget = null,
}: {
	tableOptionsPortalTarget?: HTMLElement | null;
}) {
	const canEditForm = useCanCreateFormLayout();
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["Name"] as string[],
		"OrderBy.Descending": false,
	});
	const [selectedRecordType, setSelectedRecordType] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const viewAllOrgsQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ViewAllOrganizations",
			entityType: null,
			entityId: null,
		}),
	);
	const isPlatformAdmin = viewAllOrgsQuery.data ?? false;

	const hasAppliedPersistedSettings = useRef(false);

	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		savePageSize,
	} = useTableSettings<Record<string, never>>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: {},
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;
		if (tableSettings.searchTerm) setSearchTerm(tableSettings.searchTerm);
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [settingsLoaded, tableSettings.searchTerm, tableSettings.pageSize]);

	const listParams = useMemo<RecordTypesListParams>(() => ({}), []);

	const searchTrimmed = searchTerm.trim();

	const mergedServerParams = useMemo(
		() => ({
			...listParams,
			...sortParams,
			...(searchTrimmed ? { SearchTerm: searchTrimmed } : {}),
		}),
		[listParams, sortParams, searchTrimmed],
	);

	const apiStart = pageIndex * pageSize + 1;

	const countQuery = useQuery({
		...recordQueryOptions.typesCount(mergedServerParams),
	});

	const unfilteredCountQuery = useQuery({
		...recordQueryOptions.typesCount(listParams),
	});

	const pageQuery = useQuery({
		...recordQueryOptions.typesPage(apiStart, pageSize, mergedServerParams),
	});

	const data = useMemo(() => pageQuery.data ?? [], [pageQuery.data]);

	const pageCount = useMemo(() => {
		const total = countQuery.data ?? 0;
		if (!total) return 0;
		return Math.ceil(total / pageSize);
	}, [countQuery.data, pageSize]);

	const exportMutation = useExportRecordTypes();

	const onSortChange = useCallback(
		({ fields, descending }: { fields: string[]; descending: boolean }) => {
			setSortParams({
				"OrderBy.Fields": fields,
				"OrderBy.Descending": descending,
			});
		},
		[],
	);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await queryClient.invalidateQueries({
				queryKey: recordKeys.types(),
			});
		} finally {
			setIsRefreshing(false);
		}
	};

	const navigateFormEditor = useCallback(
		(recordTypeId: string) => {
			void navigate({
				to: "/admin/configuration/recordTypes/$recordTypeId/full/form",
				params: { recordTypeId },
				search: {
					tab: "details",
					mode: "view",
					presentation: "full",
				},
			});
		},
		[navigate],
	);

	const navigateFullPage = useCallback(
		(recordTypeId: string) => {
			void navigate({
				to: "/admin/configuration/recordTypes/$recordTypeId/full",
				params: { recordTypeId },
				search: {
					tab: "details",
					mode: "view",
					presentation: "full",
				},
			});
		},
		[navigate],
	);

	const duplicateRecordTypeMutation = useDuplicateRecordType();
	const tableAnchorRef = useRef<HTMLDivElement>(null);
	const duplicateRecordType = useCallback(
		(recordTypeId: string) => {
			duplicateRecordTypeMutation.mutate(
				{
					recordTypeId,
					organizationId: isPlatformAdmin ? EMPTY_ORG_ID : undefined,
				},
				{
					onSuccess: (result) => {
						if (result.affectedId) {
							void navigate({
								to: "/admin/configuration/recordTypes/$recordTypeId/details",
								params: { recordTypeId: result.affectedId },
								search: { mode: "view", presentation: "drawer" },
							});
						}
					},
					onError: (error) => {
						showApiErrorToast({
							error,
							message: t("admin.recordTypes.modal.createError"),
						});
					},
				},
			);
		},
		[duplicateRecordTypeMutation, isPlatformAdmin, navigate, t],
	);

	const handleRowClick = useCallback(
		(row: RecordType) => {
			void navigate({
				to: "/admin/configuration/recordTypes/$recordTypeId/details",
				params: { recordTypeId: row.id },
				search: { mode: "view", presentation: "drawer" },
			});
		},
		[navigate],
	);

	const columnLabels = useMemo(
		() => ({
			name: t("admin.recordTypes.table.columns.name"),
			description: t("admin.recordTypes.table.columns.description"),
			source: t("admin.recordTypes.table.columns.source"),
			form: t("admin.recordTypes.table.columns.form"),
			id: t("admin.recordTypes.table.columns.id"),
		}),
		[t],
	);

	const handleExport = useCallback(
		(visibleColumns?: string[]) => {
			const totalCount = countQuery.data ?? 0;
			if (totalCount === 0 || totalCount > 10000) return;

			const allIds = DEFAULT_COLUMN_ORDER;
			const ids = (visibleColumns?.length ? visibleColumns : allIds).filter(
				(c) => c !== "actions",
			);

			const apiColumnById: Record<string, string> = {
				name: "Name",
				description: "Description",
				source: "Source",
				id: "Id",
			};
			const apiColumns = ids
				.map((c) => apiColumnById[c])
				.filter(Boolean)
				.join(",");

			exportMutation.mutate(
				{
					startIndex: 1,
					count: totalCount,
					params: {
						...mergedServerParams,
						...(apiColumns ? { columns: apiColumns } : {}),
					},
				},
				{
					onSuccess: (blob) => {
						downloadBlob(blob, generateExportFilename("record-types"));
					},
				},
			);
		},
		[countQuery.data, exportMutation, mergedServerParams],
	);

	const columns = useMemo<ColumnDef<RecordType>[]>(() => {
		return [
			{
				id: "name",
				accessorKey: "name",
				header: columnLabels.name,
				enableSorting: true,
				meta: {
					headerProps: { isRowHeader: true },
				},
				minSize: 280,
				cell: ({ row }) => {
					const rt = row.original;
					const Icon = resolveRecordTypeIcon(rt.icon);
					return (
						<div className="flex items-center gap-3">
							<EntityTypeFeaturedIcon icon={Icon} />
							<div className="min-w-0">
								{rt.name ? (
									<p className="text-sm font-medium text-primary">{rt.name}</p>
								) : (
									<EmptyValue />
								)}
							</div>
						</div>
					);
				},
			},
			{
				id: "description",
				accessorKey: "description",
				header: columnLabels.description,
				enableSorting: true,
				minSize: 220,
				cell: ({ row }) => {
					return row.original.description ? (
						<span className="text-sm font-medium text-primary">
							{row.original.description}
						</span>
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "source",
				accessorFn: (row) => (row.defaultType ? "system" : "custom"),
				header: columnLabels.source,
				enableSorting: false,
				minSize: 120,
				cell: ({ row }) => (
					<Label size="md" type="dark" showIcon={false}>
						{row.original.defaultType
							? t("admin.recordTypes.table.source.system")
							: t("admin.recordTypes.table.source.custom")}
					</Label>
				),
			},
			{
				id: "form",
				header: columnLabels.form,
				enableSorting: false,
				minSize: 120,
				cell: ({ row }) => (
					<TableCellContent
						type={TableCellContentType.Secondary}
						primary={
							row.original.hasForm === true
								? t("common.actions.yes")
								: t("common.actions.no")
						}
					/>
				),
			},
			{
				id: "id",
				accessorKey: "id",
				header: columnLabels.id,
				enableSorting: true,
				minSize: 200,
				cell: ({ row }) => {
					return row.original.id ? (
						<CopyableId value={row.original.id} />
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "actions",
				header: getActionsColumnHeader(t),
				enableSorting: false,
				enableHiding: false,
				size: 56,
				meta: {
					disableColumnReorder: true,
					rowFullViewAction: (row: unknown) => {
						const id = getTableRowId(row);
						if (id) navigateFullPage(id);
					},
					cellProps: {
						className: "w-14 px-2",
						"data-suppress-row-click": true,
					},
				},
				cell: ({ row }) => {
					const rt = row.original;
					return (
						<div className="flex justify-end" data-suppress-row-click>
							<Dropdown.Root>
								<Dropdown.DotsButton
									data-testid="recordtype-table-row-actions-btn"
									aria-label={t(
										"admin.recordTypes.table.actionsMenu.ariaLabel",
									)}
								/>
								<Dropdown.Popover className="w-min">
									<Dropdown.Menu>
										<Dropdown.Item
											id="view"
											label={t(
												"admin.recordTypes.table.actions.viewFullDetails",
											)}
											icon={Expand03}
											onAction={() => navigateFullPage(rt.id)}
										/>
										{canEditForm ? (
											<Dropdown.Item
												id="edit"
												label={t("admin.recordTypes.table.actions.editForm")}
												icon={Edit05}
												onAction={() => navigateFormEditor(rt.id)}
											/>
										) : null}
										<Dropdown.Item
											id="duplicate"
											data-testid="recordtype-table-action-duplicate"
											label={t("admin.recordTypes.table.actions.duplicate")}
											icon={Copy01}
											isDisabled={
												duplicateRecordTypeMutation.isPending &&
												duplicateRecordTypeMutation.variables?.recordTypeId ===
													rt.id
											}
											onAction={() => duplicateRecordType(rt.id)}
										/>
										<RecordTypeDeleteAction
											rt={rt}
											onDelete={() =>
												setSelectedRecordType({
													id: rt.id,
													name: rt.name ?? rt.id,
												})
											}
										/>
									</Dropdown.Menu>
								</Dropdown.Popover>
							</Dropdown.Root>
						</div>
					);
				},
			},
		];
	}, [
		canEditForm,
		columnLabels,
		duplicateRecordType,
		duplicateRecordTypeMutation.isPending,
		duplicateRecordTypeMutation.variables?.recordTypeId,
		navigateFormEditor,
		navigateFullPage,
		t,
	]);

	useEffect(() => {
		const total = countQuery.data ?? 0;
		if (!total) return;
		const pages = Math.max(1, Math.ceil(total / pageSize));
		if (pageIndex > 0 && pageIndex >= pages) {
			setPageIndex(Math.max(0, pages - 1));
		}
	}, [countQuery.data, pageSize, pageIndex]);

	const loadError = pageQuery.error ?? countQuery.error;

	if (loadError) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<h3 className="text-sm font-medium text-error">
					{t("admin.recordTypes.errors.failedToLoadRecordTypes")}
				</h3>
				<p className="mt-1 text-sm text-tertiary">{loadError.message}</p>
				<Button
					size="sm"
					color="primary"
					onClick={handleRefresh}
					className="mt-3"
				>
					{t("common.actions.tryAgain")}
				</Button>
			</div>
		);
	}

	return (
		<>
			<div ref={tableAnchorRef} className="w-full">
				<CustomTableWrapper<RecordType>
					tableKey={TABLE_KEY}
					data={data}
					columns={columns}
					showHeaderPadding={false}
					zebraStriping
					isLoading={pageQuery.isLoading || countQuery.isLoading}
					isFetching={
						pageQuery.isFetching ||
						countQuery.isFetching ||
						unfilteredCountQuery.isFetching
					}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={({ pageSize: ps, pageIndex: pi }) => {
						if (ps !== pageSize) {
							setPageSize(ps);
							setPageIndex(0);
							savePageSize(ps);
						} else if (pi !== pageIndex) {
							setPageIndex(pi);
						}
					}}
					pageCount={pageCount}
					rowCount={countQuery.data}
					totalRowCount={unfilteredCountQuery.data}
					sortFieldMapping={serverSortFieldMapping}
					defaultSortField="name"
					onSortChange={onSortChange}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					filterPlaceholder={t("admin.recordTypes.table.search.placeholder")}
					filterValue={searchTerm}
					filterInputTestId="recordtype-table-search-input"
					onDebouncedFilterChange={(s) => {
						setSearchTerm(s);
						setPageIndex(0);
						saveSearchTerm(s);
					}}
					enableTableActions
					filterLayout="bar"
					filterBarType="landing"
					filterBarClassName="pt-0"
					tableOptionsPortalTarget={tableOptionsPortalTarget}
					hasActiveFilters={searchTrimmed.length > 0}
					enableColumnReordering
					enableColumnResizing
					onRowClick={handleRowClick}
					getRowId={(row) => row.id}
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={tableSettings.columnVisibility}
					settingsLoaded={settingsLoaded}
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
					initialSorting={DEFAULT_SORTING}
				/>
			</div>

			{!pageQuery.isLoading &&
			!pageQuery.isFetching &&
			!countQuery.isFetching &&
			(countQuery.data ?? 0) === 0 &&
			searchTrimmed.length === 0 ? (
				<div className="flex justify-center py-12 text-gray-500">
					{t("admin.recordTypes.table.empty.noRecordTypes")}
				</div>
			) : null}

			{!pageQuery.isLoading &&
			!pageQuery.isFetching &&
			!countQuery.isFetching &&
			(countQuery.data ?? 0) === 0 &&
			searchTrimmed.length > 0 ? (
				<TableSearchEmptyState titleKey="admin.recordTypes.table.empty.noSearchResults" />
			) : null}

			<DeleteRecordTypeModal
				isOpen={selectedRecordType !== null}
				setIsOpen={(open) => {
					if (!open) setSelectedRecordType(null);
				}}
				handleClose={() => setSelectedRecordType(null)}
				onDeleteSuccess={() => setSelectedRecordType(null)}
				recordTypeId={selectedRecordType?.id ?? ""}
				recordTypeName={selectedRecordType?.name ?? ""}
				organizationId={isPlatformAdmin ? EMPTY_ORG_ID : undefined}
			/>
			<DuplicatingEntityTypeModal
				isOpen={duplicateRecordTypeMutation.isPending}
				anchorRef={tableAnchorRef}
				label={t("admin.recordTypes.duplicate.inProgress")}
				testId="recordtype-duplicate-in-progress"
			/>
		</>
	);
}

function RouteComponent() {
	const { t } = useAppTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isFullRoute = /\/configuration\/recordTypes\/[^/]+\/full/.test(
		pathname,
	);

	const canCreateRecordType = useQuery(
		authQueryOptions.checkAction({
			name: "CreateRecordType",
		}),
	);

	if (isFullRoute) {
		return <Outlet />;
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("admin.recordTypes.table.title")}
						description={t("admin.recordTypes.table.platformDescription")}
						actions={
							canCreateRecordType.data
								? [
										{
											label: t(
												"admin.recordTypes.table.actions.createRecordType",
											),
											color: "accent",
											onPress: () => setIsOpen(true),
											testId: "recordtype-table-create-btn",
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<RecordTypesTable tableOptionsPortalTarget={tableOptionsHost} />
			</PageLayout>

			{canCreateRecordType.data ? (
				<CreateRecordTypeModal
					isOpen={isOpen}
					setIsOpen={setIsOpen}
					handleClose={() => {
						setIsOpen(false);
					}}
				/>
			) : null}
			<Outlet />
		</>
	);
}
